import React, { useMemo, useState, useRef, useCallback } from 'react';
import {
  Card,
  Segment,
  Text,
  Pagination,
  ConfigProvider,
  StatusTip,
} from 'tea-component';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import { useRequest, useSetState } from 'ahooks';
import { getChatHomeData } from '@/pages/datalab/pages/BehaviorAnalysis/GptAnalysis/service';
import SvgIcon from '@/pages/home/components/SvgIcon';

const SegmentIconType = {
  active: 'flag-m',
  pay: 'dollar',
  retention: 'user-ua',
  churn: 'engagement',
  new: 'new-user-plus',
};

export default function (props: { clickQuestion: (...args:any[])=>void; getAnswerCount: (...args:any[])=>void; }) {
  const { clickQuestion, getAnswerCount } = props;
  const questionRef = useRef(null);
  const { dblFormatMessage: gptFormatMessage, isEn } = useDblIntl('gpt_home');
  const [tagValue, setTagValue] = useSetState<{ [key in string]: string }>({
    lableValue: 'all',
    subLableValue: 'all',
  });

  const [pageInfo, setPageInfo] = useState({ pageIndex: 1, pageSize: 200 });
  const { lableValue, subLableValue } = tagValue;
  const { data, loading, error, run } = useRequest(
    (params) => {
      const defaultParams = {
        type: lableValue,
        page: pageInfo.pageIndex,
        pagesize: pageInfo.pageSize,
        ...params,
      };
      return getChatHomeData(defaultParams);
    },
    {
      onSuccess(data) {
        getAnswerCount(data?.prompt_count || 0);
      },
    },
  );
  // 二级
  const options = useMemo(() => {
    const list =
      data?.questions.filter((task) => task.sub_label === subLableValue) || [];
    let subLabels = [];
    list.forEach((obj) => {
      const labels = obj.label.split(',');
      subLabels = [...subLabels, ...labels, 'all'];
    });

    subLabels = Array.from(new Set(subLabels));
    const dataSource = data?.label.filter(
      (task) => subLableValue === 'all' || subLabels.includes(task.type),
    );
    return (
      dataSource?.map((tag) => ({
        value: tag?.type,
        text: (
          <>
            <SvgIcon type={SegmentIconType[tag?.type]} />
            {isEn ? tag?.name_en : tag?.name}
          </>
        ),
      })) || []
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEn, data?.label, subLableValue]);
  // 一级
  const options2 = useMemo(() => {
    const defauAll = {
      text: isEn ? 'All' : '全部',
      value: 'all',
    };
    const res =
      data?.sub_label?.map((tag) => ({
        value: tag?.type,
        text: isEn ? tag?.name_en : tag?.name,
      })) || [];
    return [defauAll, ...res];
  }, [isEn, data?.sub_label]);
  /**
 * 前端纯分页处理
 * @param array  传入的数据源
 * @param size   数据源size大小
 * @returns 返回切割后的二维新数组
 */
  const getPagingInterception = useCallback(
    (array: string | any[], size: number) => {
      const length = array?.length;
      if (!length || !size || size < 1) {
        return [];
      }
      let index = 0; // 用来表示切割元素的范围start
      let resIndex = 0; // 用来递增表示输出数组的下标
      const result = new Array(Math.ceil(length / size));
      // 进行循环
      while (index < length) {
        // eslint-disable-next-line no-plusplus
        result[resIndex++] = array.slice(index, (index += size));
      }
      return result;
    },
    [],
  );
  const [optionsQuestions,optionsQuestionsLength] = useMemo(() => {
    const res = data?.questions
      .filter(
        (task) => subLableValue === 'all' || task.sub_label === subLableValue,
      )
      .filter((task) =>
        lableValue === 'all' ? true : task.label.indexOf(lableValue) !== -1,
      );
    const dataSource = getPagingInterception(res, pageInfo.pageSize)[
      pageInfo.pageIndex - 1
    ];
    return [dataSource,res?.length];
  }, [data, subLableValue, lableValue, pageInfo.pageSize, pageInfo.pageIndex,getPagingInterception]);

  const renderElement = useMemo(() => () => {
    if (loading || error) {
      return <StatusTip status={loading ? 'loading' : 'error'} />;
    }
    if (optionsQuestions?.length <= 0) {
      return <div className="empty">暂无数据</div>;
    }
    return (
      <div className={'db-datalab-list-table-wrap'}>
        {optionsQuestions?.map((question, index) => {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { hint, hint_en, content, content_en } = question || {};
          const title = isEn ? content_en : content;
          return (
            <div
              key={index}
              className={'list-item'}
              onClick={() => clickQuestion(title)}
            >
              <div>
                {/* <BubbleEllipsis content={title} maxWidth={questionRef?.current?.offsetWidth - 70}/>*/}
                <Text parent={'p'} overflow className={'tea-h5'}>
                  {title}
                  {lableValue === 'all' && index < 3 && pageInfo.pageIndex === 1
                    ? '🔥'
                    : ''}
                </Text>
                <Text parent={'p'} theme={'label'} overflow>
                  {isEn ? hint_en : hint}
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [clickQuestion, error, isEn, lableValue, loading, optionsQuestions, pageInfo.pageIndex]);
  
  return (
    <ConfigProvider locale={isEn ? 'en' : 'zh'}>
      <Card className={'dbl-gpt-home-questions'}>
        <Card.Body
          title={
            <div className={'dbl-title-16'}>
              {gptFormatMessage('questions_title')}
            </div>
          }
          ref={questionRef}
        >
          <Segment
            className={'question-segment'}
            options={options2}
            value={subLableValue}
            onChange={(value) => {
              run({ type: value, page: 1, pagesize: 200 });
              setTagValue({ subLableValue: value });
              setPageInfo({ pageIndex: 1, pageSize: 200 });
            }}
          />
          <div
            style={{
              display: 'inline-block',
              marginLeft: '10px',
            }}
          >
            <Segment
              className={'question-segment'}
              options={options}
              value={lableValue}
              onChange={(value) => {
                setTagValue({ lableValue: value });
                setPageInfo({ pageIndex: 1, pageSize: 200 });
              }}
            />
          </div>
          <div className={'list-table'}>
            {renderElement()}
            <Pagination
              className={'list-pagination'}
              recordCount={
                optionsQuestionsLength
              }
              stateText={gptFormatMessage('table_total', {
                total:
                optionsQuestionsLength
              })}
              pageIndex={pageInfo.pageIndex}
              pageSize={pageInfo.pageSize}
              onPagingChange={(query) => {
                setPageInfo(query);
              }}
            />
          </div>
        </Card.Body>
      </Card>
    </ConfigProvider>
  );
}
